// /invite/accept — lands an invited user's session (T338).
//
// AdminUsers' invite-creation flow hands out a link of the form
// `/invite/accept#token=<invite-token>` (URL fragment, T9310 — never sent to servers or logs) (see CreateInvitationHandler's
// `accept_url` in api/internal/handlers/invitations.go). Unlike the SSO
// callbacks (T337, which redirect the browser straight to
// /login/callback?token=<jwt>), this page calls the backend accept endpoint
// itself via fetch so an expired/invalid/already-used token renders a clear
// in-app error instead of a bare JSON body. On success the backend returns
// the issued JWT in the response, which this page stores via setToken() —
// the same plumbing LoginCallback uses.

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiRequest, ApiError } from '../lib/api';
import { setToken } from '../lib/auth';
import { buttonClasses, Card } from '../components/ui';

const ERROR_MESSAGES: Record<string, string> = {
  missing_token: 'This invitation link is missing its token.',
  invalid_token: 'This invitation link is invalid.',
  token_expired: 'This invitation link has expired. Ask an admin to send a new invite.',
  token_used: 'This invitation has already been accepted. Try signing in instead.',
};

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return ERROR_MESSAGES[err.code] ?? 'Something went wrong accepting your invitation.';
  }
  return 'Something went wrong accepting your invitation.';
}

export function InviteAccept() {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Guards against React StrictMode's double-effect in dev: the accept
  // endpoint is single-use server-side, so firing it twice would surface a
  // spurious "already accepted" error on the very first visit.
  const requested = useRef(false);

  useEffect(() => {
    const token = new URLSearchParams(location.hash.replace(/^#/, '')).get('token');
    if (!token) {
      setError(ERROR_MESSAGES.missing_token);
      return;
    }
    if (requested.current) return;
    requested.current = true;

    apiRequest<{ token: string }>('/auth/invite/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
      .then((resp) => {
        setToken(resp.token);
        navigate('/', { replace: true });
      })
      .catch((err: unknown) => {
        setError(errorMessage(err));
      });
  }, [location.hash, navigate]);

  if (!error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <p className="text-sm text-fg-muted">Setting up your account…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <Card className="w-full max-w-sm p-8">
        <p className="orb-eyebrow text-danger mb-2">invitation error</p>
        <p className="text-sm text-fg mb-6">{error}</p>
        <Link to="/login" className={buttonClasses()}>
          Go to sign in
        </Link>
      </Card>
    </div>
  );
}
